import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { Shop, TicketApi } from '@/lib/TicketApi';
import { shopHasTieredProducts } from '@/lib/previousTier';

const TIER_REFRESH_INTERVAL_MS = 30_000;
import { eventEmitter, ReloadShopConfigPayload } from '@/lib/eventEmitter';
import * as m from "@/paraglide/messages.js"
import { subscribeToLanguageChange } from '@/lib/languageChange';
import { useLanguage } from './LanguageProvider';
import { StyleOverwrites } from './StyleOverwrites';
import { useTracking } from '@/tracking/context';
import * as Sentry from "@sentry/react";

interface ShopContextType {
  shop: Shop;
  isLoading: boolean;
  shopSlug: string;
  baseUrl: string;
  isWidget: boolean;
  isStandalone: boolean;
  error: string | null;
  selectedEventSlug: string | undefined;
  initialEventSlug: string | undefined;
  setSelectedEventSlug: (eventSlug: string | undefined) => void;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

interface ShopProviderProps {
  shop?: Shop;
  shopSlug: string;
  baseUrl: string;
  children: ReactNode;
  isWidget?: boolean;
  isStandalone?: boolean;
  initialEventSlug?: string;
  usePageBackground?: boolean;
}

export interface ShopParams {
  language: string;
  unlockedProductIds: string[];
}

const PageBackground: React.FC<{ shop: Shop; children: ReactNode }> = ({ shop, children }) => {
  const { isWidget } = useShop();
  const hasBackgroundImage = !!shop.style.pageBackgroundImage;
  const style = {
    background: hasBackgroundImage
      ? `url(${shop.style.pageBackgroundImage}) center/cover fixed no-repeat, linear-gradient(to bottom, ${shop.style.pageBackgroundFromColor || '#ffffff'}, ${shop.style.pageBackgroundToColor || '#ffffff'})`
      : `linear-gradient(to bottom, ${shop.style.pageBackgroundFromColor || '#ffffff'}, ${shop.style.pageBackgroundToColor || '#ffffff'})`
  };
  
  return (
    <div className="min-h-screen sm:p-10" style={style}>
      <div className={`max-w-[800px] mx-auto ${isWidget ? 'rounded-md' : 'rounded-3xl'} pb-6 shadow-2xl mb-[20px]`} style={{
        backgroundColor: shop.style.panelBackgroundColor || '#ffffff',
      }}>
        {children}
      </div>
    </div>
  );
};

export const ShopProvider: React.FC<ShopProviderProps> = ({ 
  shopSlug, 
  children, 
  shop: initialShop, 
  baseUrl, 
  isWidget = false,
  isStandalone = false,
  initialEventSlug,
  usePageBackground = false 
}) => {
  const [shop, setShop] = useState<Shop | null>(initialShop ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEventSlug, setSelectedEventSlug] = useState<string | undefined>(initialEventSlug);
  const [cachedShops, setCachedShops] = useState<Record<string, Shop>>({});
  const [loadedTrackers, setLoadedTrackers] = useState<string[]>([]);
  const trackedPageViews = useRef<Set<string>>(new Set());

  const trackingProvider = useTracking();
  const { trackEvent } = useTracking();
  const { currentLanguage: lang } = useLanguage();

  const [shopParams, setShopParams] = useState<ShopParams>({
    language: lang,
    unlockedProductIds: [],
  });

  // Mirrors the params of the most recent fetch. The language change listener below is registered
  // once and would otherwise close over the params from first render.
  const fetchedParams = useRef<ShopParams>(shopParams);

  useEffect(() => {
    setLoadedTrackers([]);
    trackingProvider.resetPixelConfigs(shopSlug).catch(err => {
      console.error('Failed to reset tracking configs:', err);
    });

    return () => {
      trackingProvider.resetPixelConfigs(null).catch(err => {
        console.error('Failed to cleanup tracking configs:', err);
      });
    };
  }, [shopSlug, trackingProvider]);

  const fetchShopConfig = async (params: ShopParams) => {
    fetchedParams.current = params;
    setError(null);
    const cacheKeyForShop = `${shopSlug}-${params.language}`;

    // Only check cache if there are no unlocked products
    if (params.unlockedProductIds.length === 0 && cachedShops[cacheKeyForShop]) {
      setShop(cachedShops[cacheKeyForShop]);
      return;
    }

    const MAX_RETRIES = 5;
    const INITIAL_DELAY = 500; // 500ms initial delay

    let lastError: unknown;
    
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt === 0) {
          setIsLoading(true);
        }

        const ticketApi = new TicketApi(baseUrl, shopSlug);
        const shop = await ticketApi.loadShop(params.language, params.unlockedProductIds);
        setShop(shop);
        
        // Only cache if there are no unlocked products
        if (params.unlockedProductIds.length === 0) {
          setCachedShops(prev => ({...prev, [cacheKeyForShop]: shop}));
        }

        setIsLoading(false);
        return; // Success, exit the retry loop
      } catch (err) {
        lastError = err;
        console.warn(`Failed to load shop config (attempt ${attempt + 1}/${MAX_RETRIES}):`, err);
        
        if (attempt < MAX_RETRIES - 1) {
          // Calculate delay with exponential backoff: 500ms, 1000ms, 2000ms, 4000ms...
          const delay = INITIAL_DELAY * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // If we get here, all retries failed
    setIsLoading(false);
    setError(lastError instanceof Error ? lastError.message : 'Failed to load shop after multiple attempts');
  };

  // FL-581 — a shop selling tiers (Early bird → Regular) refreshes itself, so the next tier
  // opens within one interval of the one before it selling out, without a reload. Shops
  // without tiers never poll.
  const hasTiers = shop !== null && shopHasTieredProducts(shop);
  useEffect(() => {
    if (!hasTiers) {
      return;
    }
    const intervalId = setInterval(async () => {
      try {
        const params = fetchedParams.current;
        const fresh = await new TicketApi(baseUrl, shopSlug).loadShop(params.language, params.unlockedProductIds);
        setShop(fresh);
        if (params.unlockedProductIds.length === 0) {
          setCachedShops(prev => ({...prev, [`${shopSlug}-${params.language}`]: fresh}));
        }
      } catch (err) {
        console.warn('Failed to refresh shop with ticket tiers:', err);
      }
    }, TIER_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasTiers, baseUrl, shopSlug]);

  // Combined effect for initial load and language changes
  useEffect(() => {
    fetchShopConfig(shopParams).then(() => {
      // Only track page view if we haven't tracked it for this shop yet
      const pageViewKey = `${shopSlug}-${window.location.pathname}`;
      if (!trackedPageViews.current.has(pageViewKey)) {
        trackedPageViews.current.add(pageViewKey);
        trackEvent({
          type: 'PAGE_VIEW',
          payload: {
            shopId: shopSlug,
            path: window.location.pathname,
            title: document.title,
            referrer: document.referrer,
            params: Object.fromEntries(new URLSearchParams(window.location.search))
          }
        });
      }
    });

    // Set up language change listener
    const languageChangeHandler = (newLanguageTag: string) => {
      // Refetch whenever the resolved language differs from the one we actually fetched with — never
      // skip on "first" change. LanguageProvider resolves the stored/browser preference in an effect,
      // and effects run child-first, so our initial fetch can already have gone out under the wrong
      // language. Skipping that first change left API-supplied content (product names, descriptions,
      // fee labels) in English while the rest of the UI switched to Dutch.
      if (newLanguageTag === fetchedParams.current.language) {
        return;
      }

      const newParams = {
        ...fetchedParams.current,
        language: newLanguageTag,
      };
      setShopParams(newParams);
      fetchShopConfig(newParams);
    };

    return subscribeToLanguageChange(languageChangeHandler);
  }, [shopSlug, trackEvent]); // Added trackEvent to dependencies

  useEffect(() => {
    if (shop?.conversionTracking?.trackers) {
      for (const tracker of shop.conversionTracking.trackers) {
        if (!loadedTrackers.includes(tracker.id)) {
          trackingProvider.addPixelConfig(tracker);
          setLoadedTrackers(prev => [...prev, tracker.id]);
        }
      }
    }
  }, [shop]);

  useEffect(() => {
    const handleReloadShopConfig = (payload: ReloadShopConfigPayload) => {
      if (payload.shopSlug !== shopSlug) {
        return;
      }

      const hasLanguageChanged = payload.language !== undefined && payload.language !== shopParams.language;
      const hasUnlockedProductsChanged = payload.unlockedProductIds !== undefined && 
        (payload.unlockedProductIds.length !== shopParams.unlockedProductIds.length || 
         payload.unlockedProductIds.some(id => !shopParams.unlockedProductIds.includes(id)));

      if (!hasLanguageChanged && !hasUnlockedProductsChanged) {
        return;
      }

      const newShopParams = {
        language: payload.language ?? shopParams.language,
        unlockedProductIds: payload.unlockedProductIds ?? shopParams.unlockedProductIds,
      };

      setShopParams(newShopParams);
      fetchShopConfig(newShopParams);
    };

    eventEmitter.on('RELOAD_SHOP_CONFIG', handleReloadShopConfig);
    return () => eventEmitter.off('RELOAD_SHOP_CONFIG', handleReloadShopConfig);
  }, [shopParams]); // Add shopParams as dependency

  // Add effect to handle automatic event selection when there's only one event
  useEffect(() => {
    if (shop?.events.length === 1 && !selectedEventSlug) {
      setSelectedEventSlug(shop.events[0].slug);
    }
  }, [shop?.events, selectedEventSlug]);

  // Add event listeners for shop-open and shop-close
  useEffect(() => {
    const handleShopOpen = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail.shopSlug === shopSlug) {
        setSelectedEventSlug(customEvent.detail.eventSlug || undefined);
      }
    };

    const handleShopClose = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail.shopSlug === shopSlug) {
        setSelectedEventSlug(undefined);
      }
    };

    window.addEventListener('shop-open', handleShopOpen);
    window.addEventListener('shop-close', handleShopClose);

    return () => {
      window.removeEventListener('shop-open', handleShopOpen);
      window.removeEventListener('shop-close', handleShopClose);
    };
  }, [shopSlug]);

  // Log to Sentry if still loading after 5 seconds
  const sentryWarningFired = useRef(false);
  useEffect(() => {
    if (shop !== null) return;
    const timeout = setTimeout(() => {
      if (shop === null && !sentryWarningFired.current) {
        sentryWarningFired.current = true;
        Sentry.captureMessage('Shop stuck in loading state for more than 5 seconds', {
          level: 'warning',
          extra: { shopSlug, error, isLoading },
        });
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [shop, shopSlug, error, isLoading]);

  if (shop === null) {
    const skeleton = (
      <div className={error ? '' : 'animate-pulse'}>
        {/* Header image skeleton */}
        <div className={`h-28 bg-gray-200 ${isWidget ? 'rounded-t-md' : 'rounded-t-3xl'}`} />

        {/* Header info bar skeleton */}
        <div className="px-8 py-4 space-y-2">
          <div className="h-5 bg-gray-200 rounded w-3/5" />
          <div className="h-3 bg-gray-200 rounded w-2/5" />
        </div>

        {/* Product section skeleton */}
        <div className="px-8 pb-6 space-y-4">
          {/* Section title */}
          <div className="h-4 bg-gray-200 rounded w-1/3" />

          {/* Product cards */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-gray-200 rounded-md shadow-sm p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/4" />
                </div>
                <div className="h-8 w-20 bg-gray-200 rounded ml-4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    const errorOverlay = error && (
      <div className="flex flex-col items-center px-8 pb-6 text-center gap-3">
        <p className="text-gray-500 text-sm">{m.shop_load_failed()}</p>
        <button
          onClick={() => fetchShopConfig(shopParams)}
          className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
        >
          {m.retry()}
        </button>
      </div>
    );

    return usePageBackground ? (
      <div className="min-h-screen sm:p-10">
        <div className={`max-w-[800px] mx-auto ${isWidget ? 'rounded-md' : 'rounded-3xl'} pb-6 shadow-2xl mb-[20px] bg-white overflow-hidden`}>
          {skeleton}
          {errorOverlay}
        </div>
      </div>
    ) : (
      <div className={`${isWidget ? 'rounded-md' : 'rounded-3xl'} bg-white overflow-hidden`}>
        {skeleton}
        {errorOverlay}
      </div>
    );
  }

  const value = {
    shop,
    isLoading,
    error,
    shopSlug,
    baseUrl,
    isWidget: isWidget ?? false,
    isStandalone: isStandalone ?? false,
    selectedEventSlug,
    initialEventSlug,
    setSelectedEventSlug,
  };

  return (
    <ShopContext.Provider value={value}>
      <StyleOverwrites shopStyle={shop.style}>
        {usePageBackground ? (
          <PageBackground shop={shop}>
            {children}
          </PageBackground>
        ) : (
          children
        )}
      </StyleOverwrites>
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

export const useShopWhenAvailable = () => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    return null;
  }
  return context;
};
